import { Box, Typography, Sheet } from '@mui/joy';

/**
 * «Ваша статистика» в духе UWorld: кольцо точности (верно / неверно / пропущено),
 * кольцо использования банка и три списка. Данные из /testing/statistics/user/.
 */
const LIME = '#65A30D';
const CORAL = '#B84C31';
const SKY = '#2A75D3';
const TRACK = 'rgba(120,120,120,0.16)';

const fmt = (n) => (n ?? 0).toLocaleString('ru-RU').replace(/ /g, ' ');

/** Кольцо из сегментов: segments = [{ value, color }], label по центру. */
function Donut({ id, segments, centerValue, centerLabel, size = 132, stroke = 11 }) {
    const r = (size - stroke) / 2;
    const C = 2 * Math.PI * r;
    const total = segments.reduce((a, s) => a + (s.value || 0), 0);
    let offset = 0;
    return (
        <Box data-testid={`donut-${id}`} sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
                {total > 0 && segments.map((s, i) => {
                    const len = (s.value / total) * C;
                    const el = (
                        <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={stroke}
                            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} strokeLinecap="butt" />
                    );
                    offset += len;
                    return el;
                })}
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>{centerValue}</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>{centerLabel}</Typography>
            </Box>
        </Box>
    );
}

function Row({ id, label, value, dot }) {
    return (
        <Box data-testid={`row-${id}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.1, borderBottom: '1px solid', borderColor: 'divider', gap: 1 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                {dot && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />}
                {label}
            </Typography>
            <Box sx={{ px: 1.25, py: 0.25, borderRadius: '999px', bgcolor: 'background.level1', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {value}
            </Box>
        </Box>
    );
}

function Block({ title, children }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
            {children}
        </Box>
    );
}

export default function UserStatsCard({ stats }) {
    const s = stats || {};
    const correct = s.correct_answers ?? 0;
    const answered = s.total_questions_answered ?? 0;
    const incorrect = s.incorrect_answers ?? Math.max(0, answered - correct);
    const skipped = s.skipped_questions ?? 0;
    const acc = answered > 0 ? Math.round((correct / answered) * 100) : 0;
    const qb = s.qbank || { used: 0, unused: 0, total: 0, used_percentage: 0 };
    const usedPct = Math.round(qb.used_percentage ?? 0);
    const t = s.tests || { created: s.total_tests ?? 0, completed: s.completed_tests ?? 0, suspended: 0 };

    return (
        <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', borderColor: 'divider', bgcolor: 'background.surface' }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.015em', mb: 2.5 }}>Ваша статистика</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr 1fr' }, columnGap: { md: 4 }, rowGap: 3, alignItems: 'start' }}>
                <Donut id="accuracy" centerValue={`${acc}%`} centerLabel="верно"
                    segments={[{ value: correct, color: LIME }, { value: incorrect, color: CORAL }, { value: skipped, color: '#C8C6C0' }]} />
                <Block title="Ваш результат">
                    <Row id="correct" label="Верно" value={fmt(correct)} dot={LIME} />
                    <Row id="incorrect" label="Неверно" value={fmt(incorrect)} dot={CORAL} />
                    <Row id="skipped" label="Пропущено" value={fmt(skipped)} dot="#C8C6C0" />
                </Block>
                <Block title="Тесты">
                    <Row id="created" label="Создано" value={fmt(t.created)} />
                    <Row id="completed" label="Завершено" value={fmt(t.completed)} />
                    <Row id="suspended" label="Не завершено" value={fmt(t.suspended)} />
                </Block>

                <Donut id="used" centerValue={`${usedPct}%`} centerLabel="использовано"
                    segments={[{ value: qb.used, color: SKY }, { value: qb.unused, color: 'transparent' }]} />
                <Block title="Использование QBank">
                    <Row id="used" label="Использовано вопросов" value={fmt(qb.used)} dot={SKY} />
                    <Row id="unused" label="Не использовано" value={fmt(qb.unused)} />
                    <Row id="total" label="Всего вопросов" value={fmt(qb.total)} />
                </Block>
                <Box sx={{ display: { xs: 'none', md: 'block' } }} />
            </Box>
        </Sheet>
    );
}
